import { motion } from "framer-motion";
import { useMemo } from "react";
import ServiceCard from "./ServiceCard";
import {
  PenTool,
  FileUser,
  TrendingUp,
  Briefcase,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ServicesSection() {
  const navigate = useNavigate();

  // Composed icons with subtle layering for a premium feel
  const ComboIconATS = useMemo(
    () => (
      <div className="relative inline-flex items-center justify-center">
        <FileUser size={40} className="text-indigo-600 drop-shadow-sm" />
        <TrendingUp
          size={20}
          className="text-fuchsia-500 absolute -bottom-1 -right-1"
        />
      </div>
    ),
    []
  );

  const IndustryIcon = useMemo(
    () => (
      <div className="relative inline-flex items-center justify-center">
        <Briefcase size={40} className="text-indigo-600 drop-shadow-sm" />
        <Sparkles
          size={20}
          className="text-amber-500 absolute -top-1 -right-1"
        />
      </div>
    ),
    []
  );

  const services = [
    {
      icon: <PenTool size={40} className="text-indigo-600" />,
      title: "Profile Optimization",
      desc:
        "Sharper headlines, value-packed summaries, and recruiter-ready sections tailored to your target roles—so your profile tells a clear, credible story.",
    },
    {
      icon: ComboIconATS,
      title: "Resume Alignment + ATS Keyword Boost",
      desc:
        "We align your resume and profile end-to-end, map high-impact keywords to job targets, and structure content to pass ATS scans while staying human-friendly.",
    },
    {
      icon: IndustryIcon,
      title: "Industry-Ready Profiles — Stand Out Across Multiple Roles",
      desc:
        "Position yourself for adjacent roles with variant headlines, targeted highlights, and achievement bullets that resonate across companies and industries.",
    },
  ];

  return (
    <section
      id="services"
      className="relative py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden"
    >
      {/* Soft background accents */}
      <div className="absolute -top-20 right-0 w-72 h-72 bg-indigo-200/30 blur-[100px] rounded-full" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200/30 blur-[120px] rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-4xl font-extrabold text-gray-900 mb-14"
        >
          Our Professional Services
        </motion.h2>

        {/* Services grid */}
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          {services.map((s, i) => (
            <ServiceCard key={i} {...s} />
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <button
            onClick={() => navigate("/aboutUs")}
            className="group inline-flex items-center gap-2 px-6 py-3 text-sm md:text-base font-semibold rounded-full 
                       bg-indigo-600 text-white shadow-md hover:bg-indigo-700 transition-all duration-300"
          >
            See More
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
